/**
 * `/v1/calendar/items`, hendelsene og ICS-nøkkelen — alt KOI Del 3 la til på kalenderen.
 *
 * =================================================================================================
 * HVORFOR DENNE FILA STÅR VED SIDEN AV `queries.ts` OG IKKE I DEN.
 *
 * `queries.ts` er TELLINGENE: månedsrutenettets «fire oppgaver den fjortende». Denne er ELEMENTENE:
 * sju kilder, med tid, varighet og lås. De to ser like ut på en skjerm og er to forskjellige svar
 * med to forskjellige ferskhetskrav — tellingene tåler tretti sekunder, et skift noen nettopp ble
 * satt på gjør det ikke — og de invalideres av forskjellige ting. En fil ville skjult det.
 *
 * `CACHE.list` LIKEVEL, og det er ikke en motsigelse: kalenderen deler ferskhet med oppgavelista
 * fordi det ER oppgavelista, lest på en annen måte. Det som skiller dem er hvilke MUTASJONER som
 * rammer dem, og det står i `meta.invalidates` på hver enkelt.
 *
 * ETT `qk.calendar(orgId)`-PREFIKS INVALIDERER BEGGE. Det er hele poenget med at de deler rot: den
 * som flytter en frist trenger ikke vite at kalenderen har to former.
 */
import {
  calendarEventDtoSchema,
  calendarFeedTokenDtoSchema,
  calendarFeedTokenListSchema,
  calendarItemsResponseSchema,
  type CalendarEventDto,
  type CalendarFeedTokenDto,
  type CalendarFeedTokenList,
  type CalendarItemsResponse,
  type CalendarKind,
  type CalendarSeriesScope,
  type CreateCalendarEventRequest,
  type CreateCalendarFeedTokenRequest,
  type RespondToCalendarEventRequest,
  type UpdateCalendarEventRequest,
} from '@tvitvi/shared';
import {
  queryOptions,
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';

import { CACHE } from '@/api/cache-tiers';
import { apiRequest } from '@/api/client';
import { qk } from '@/api/query-keys';

export interface CalendarItemsFilters {
  readonly from: string;
  readonly to: string;
  readonly editionId?: string | undefined;
  readonly kinds?: readonly CalendarKind[] | undefined;
  readonly departmentId?: string | undefined;
  readonly personId?: string | undefined;
  readonly mine?: boolean | undefined;
  readonly q?: string | undefined;
}

/** Det vinduet sender, som et objekt — også filteret kalendereksporten får (docs/EKSPORT.md). */
export function calendarItemsQuery(
  filters: CalendarItemsFilters,
): Record<string, string | boolean> {
  const query: Record<string, string | boolean> = { from: filters.from, to: filters.to };
  if (filters.editionId !== undefined) query['editionId'] = filters.editionId;
  if (filters.kinds !== undefined && filters.kinds.length > 0) {
    query['kinds'] = [...filters.kinds].join(',');
  }
  if (filters.departmentId !== undefined) query['departmentId'] = filters.departmentId;
  if (filters.personId !== undefined) query['personId'] = filters.personId;
  if (filters.mine === true) query['mine'] = true;
  if (filters.q !== undefined && filters.q.length > 0) query['q'] = filters.q;
  return query;
}

/**
 * Spørringen, med nøkkelen bygget av NØYAKTIG det som sendes.
 *
 * Et filter som fantes i nøkkelen men ikke i forespørselen (eller motsatt) er en cache som serverer
 * ett spørsmåls svar på et annet. Derfor bygges begge av det samme objektet, én gang.
 */
export function calendarItemsQueryOptions(orgId: string, filters: CalendarItemsFilters) {
  const query = calendarItemsQuery(filters);

  return queryOptions({
    queryKey: qk.calendarItems(orgId, query),
    queryFn: ({ signal }): Promise<CalendarItemsResponse> =>
      apiRequest({
        path: '/calendar/items',
        query,
        schema: calendarItemsResponseSchema,
        signal,
      }),
    ...CACHE.list,
  });
}

/** Én hendelse med deltakerne sine — det panelet leser, og det vinduet ikke bærer. */
export function calendarEventQueryOptions(orgId: string, eventId: string) {
  return queryOptions({
    queryKey: qk.calendarEvent(orgId, eventId),
    queryFn: ({ signal }): Promise<CalendarEventDto> =>
      apiRequest({
        path: `/calendar/events/${eventId}`,
        schema: calendarEventDtoSchema,
        signal,
      }),
    ...CACHE.list,
  });
}

export function useCreateCalendarEvent(
  orgId: string,
): UseMutationResult<CalendarEventDto, unknown, CreateCalendarEventRequest> {
  return useMutation({
    mutationFn: (input: CreateCalendarEventRequest) =>
      apiRequest({
        path: '/calendar/events',
        method: 'POST',
        body: input,
        schema: calendarEventDtoSchema,
      }),
    meta: { invalidates: [qk.calendar(orgId)] },
  });
}

export interface UpdateCalendarEventInput {
  readonly id: string;
  readonly body: UpdateCalendarEventRequest;
  /** Én forekomst av en serie (F49), og hva endringen gjelder: bare den, den og senere, eller alle. */
  readonly occurrence?: string;
  readonly scope?: CalendarSeriesScope;
}

export function useUpdateCalendarEvent(
  orgId: string,
): UseMutationResult<CalendarEventDto, unknown, UpdateCalendarEventInput> {
  return useMutation({
    mutationFn: ({ id, body, occurrence, scope }: UpdateCalendarEventInput) =>
      apiRequest({
        path: `/calendar/events/${id}`,
        method: 'PATCH',
        ...(occurrence === undefined
          ? {}
          : { query: { occurrence, ...(scope === undefined ? {} : { scope }) } }),
        body,
        schema: calendarEventDtoSchema,
      }),
    meta: { invalidates: [qk.calendar(orgId)] },
  });
}

/** Hele hendelsen (en id), eller bare én forekomst av en serie (QA6-K6 H1). */
export type DeleteCalendarEventInput =
  | string
  | {
      readonly id: string;
      readonly occurrence: string;
      /** F49: `following` sletter den og alle etter. Utelatt er bare den ene. */
      readonly scope?: CalendarSeriesScope;
    };

export function useDeleteCalendarEvent(
  orgId: string,
): UseMutationResult<void, unknown, DeleteCalendarEventInput> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: DeleteCalendarEventInput) => {
      if (typeof input !== 'string') {
        /* Serien står igjen; bare forekomsten forsvinner, og invalideringen under henter vinduet. */
        await apiRequest({
          path: `/calendar/events/${input.id}`,
          method: 'DELETE',
          query: {
            occurrence: input.occurrence,
            ...(input.scope === undefined ? {} : { scope: input.scope }),
          },
        });
        return;
      }
      const id = input;
      await apiRequest({ path: `/calendar/events/${id}`, method: 'DELETE' });
      /*
       * DEN SLETTEDE HENDELSEN FJERNES FØR PREFIKSET INVALIDERES (QA5-B). Panelet står fortsatt
       * åpent med spørringen aktiv i det invalideringen går, og uten dette ble hendelsen hentet på
       * nytt — `GET /calendar/events/<id> -> 404` i konsollen etter hver sletting.
       */
      client.removeQueries({ queryKey: qk.calendarEvent(orgId, id), exact: true });
    },
    meta: { invalidates: [qk.calendar(orgId)] },
  });
}

export interface RespondToEventInput {
  readonly id: string;
  readonly body: RespondToCalendarEventRequest;
}

/** Å svare på sin egen invitasjon. Den eneste raden som endres er ens egen — se tjenesten. */
export function useRespondToCalendarEvent(
  orgId: string,
): UseMutationResult<CalendarEventDto, unknown, RespondToEventInput> {
  return useMutation({
    mutationFn: ({ id, body }: RespondToEventInput) =>
      apiRequest({
        path: `/calendar/events/${id}/respond`,
        method: 'POST',
        body,
        schema: calendarEventDtoSchema,
      }),
    meta: { invalidates: [qk.calendar(orgId)] },
  });
}

/**
 * ICS-NØKKELEN, OG SVARET VISES ÉN GANG. «Lag ny» trekker den gamle i samme gruppe på serveren
 * (sak 216.13), og lista over grupper invalideres så panelet viser den nye.
 *
 * Nøkkelen i `qk` bærer fortsatt en årgang fra da lista var per årgang; gruppene er personens og
 * husets, så den er alltid `null`.
 */
export function useCreateCalendarFeedToken(
  orgId: string,
): UseMutationResult<CalendarFeedTokenDto, unknown, CreateCalendarFeedTokenRequest> {
  return useMutation({
    mutationFn: (input: CreateCalendarFeedTokenRequest) =>
      apiRequest({
        path: '/calendar/feed-token',
        method: 'POST',
        body: input,
        schema: calendarFeedTokenDtoSchema,
      }),
    meta: { invalidates: [qk.calendarFeedTokens(orgId, null)] },
  });
}

/** Gruppene man kan abonnere på, med den levende lenken i hver (uten nøkkelen). */
export function calendarFeedTokensQueryOptions(orgId: string) {
  return queryOptions({
    queryKey: qk.calendarFeedTokens(orgId, null),
    queryFn: ({ signal }): Promise<CalendarFeedTokenList> =>
      apiRequest({
        path: '/calendar/feed-tokens',
        schema: calendarFeedTokenListSchema,
        signal,
      }),
    ...CACHE.list,
  });
}

/** Trekker én lenke. Den som har lagt den inn i Google, slutter å få oppdateringer. */
export function useRevokeCalendarFeedToken(
  orgId: string,
): UseMutationResult<void, unknown, string> {
  return useMutation({
    mutationFn: async (id: string) => {
      await apiRequest({ path: `/calendar/feed-token/${id}/revoke`, method: 'POST' });
    },
    meta: { invalidates: [qk.calendarFeedTokens(orgId, null)] },
  });
}

/**
 * «GI DELTAKERNE BESKJED» (253.32): endringen er lagret, og dette er den egne handlingen som sender
 * et varsel til hver deltaker. Svaret er tomt; ingenting i kalenderen er endret av det.
 */
export function useNotifyCalendarEvent(orgId: string): UseMutationResult<void, unknown, string> {
  return useMutation({
    mutationFn: (id: string) =>
      apiRequest({ path: `/calendar/events/${id}/notify`, method: 'POST' }).then(() => undefined),
    meta: { invalidates: [qk.calendar(orgId)] },
  });
}
